import type { Messages } from '../catalogue.en'

/**
 * The patient record surface: attachments, their upload, the Group and Content
 * they carry, and the patient EMR journal built on them.
 *
 * Composed into the public catalogue by `catalogue.he.ts`. Nothing imports
 * this module directly: consumers ask the catalogue, so how the messages are
 * stored stays this layer's business.
 */
export const emr: Partial<Messages> = {
  'protocolChoice.primary': "פרוטוקול ראשי",
  'protocolChoice.choose': "בחירת פרוטוקול",
  'protocolChoice.empty': "אין פרוטוקולים ראשיים פעילים",
  'protocolChoice.defaultUnavailable': "אין פרוטוקול ראשי זמין כברירת מחדל. יש לבקש ממנהל המרפאה להגדיר אחד או להעניק הרשאה לבחירת פרוטוקול.",
  'protocolChoice.replace': "החלפת הפרוטוקול הראשי",
  'protocolChoice.replacing': "מחליף…",
  'protocolChoice.confirmReplace': "מחיקת התוכן והחלפת הפרוטוקול",
  'protocolChoice.discardDetail': "כל התוכן השמור והלא שמור של הפרוטוקול הראשי הנוכחי, כולל תרגומיו, יוסר מהרשומה הנוכחית. הפרוטוקול החדש יתחיל ריק. האבחנות, הפרוטוקולים הנוספים, השירותים, ההפניות ושאר נתוני הרשומה יישמרו. הפרוטוקול הקודם יישאר בהיסטוריה.",
  'protocolChoice.current': "הפרוטוקול הנוכחי",
  'protocolChoice.new': "פרוטוקול חדש",
  'protocolChoice.notPrimary': "פרוטוקול זה הוא מסמך נוסף. יש לבחור פרוטוקול ראשי.",
  'protocolChoice.unchanged': "פרוטוקול זה כבר מוגדר כפרוטוקול הראשי הנוכחי.",
  'protocolChoice.primaryExists': "לרשומה כבר יש פרוטוקול ראשי. יש להשתמש בהחלפת הפרוטוקול הראשי.",

  'file.title': 'קבצים',
  'file.tableLabel': 'קבצי המטופל',
  'file.columnName': 'שם',
  'file.columnDocumentDate': 'תאריך המסמך',
  'file.columnUploaded': 'הועלה',
  'file.columnAuthor': 'מעלה',
  'file.columnStatus': 'סטטוס',
  'file.columnSize': 'גודל',
  'file.columnLinks': 'קישורים לרשומות המרפאה',
  'file.columnActions': 'פעולות',

  // The two date columns bound themselves through `table.rangeFrom`, off their
  // own headings. The size keeps its own pair because of the unit.
  'file.sizeFrom': 'גודל מ־ (KB)',
  'file.sizeTo': 'גודל עד (KB)',
  'file.linkStateLabel': 'סינון לפי קישור לרשומה',
  'file.linkStateAll': 'כל הקבצים',
  'file.linkStateLinked': 'מקושר לרשומה',
  'file.linkStateUnlinked': 'לא מקושר',

  'file.selectedFile': 'הקובץ שנבחר',

  'file.loading': 'טוען קבצים',
  'file.unavailable': 'רשימת הקבצים אינה זמינה',
  'file.forbidden': 'אין לך הרשאה לצפות בקבצי המטופל',
  'file.forbiddenDetail': 'שאר כרטיס המטופל עדיין זמין.',
  'file.empty': 'למטופל זה אין קבצים',
  'file.filteredEmpty': 'אין קבצים התואמים לסינון הזה',
  'file.filteredEmptyDetail': 'אפס את הסינון כדי לראות את כל הקבצים של המטופל.',
  'file.pageEmpty': 'עמוד התוצאות הזה אינו זמין עוד',
  'file.pageEmptyDetail': 'עדיין יש קבצים תואמים. חזור לעמוד הראשון כדי לראות אותם.',
  'file.firstPage': 'חזרה לעמוד הראשון',

  'file.expand': 'הצגת פרטים עבור {name}',
  'file.collapse': 'הסתרת פרטים עבור {name}',
  'file.download': 'הורדת {name}',
  'file.preview': 'הצגה',
  'file.previewNamed': 'הצגת {name}',
  'file.previewTitle': 'הצגת {name}',
  'file.previewClose': 'סגירה',
  'file.previewControls': 'פקדי מציג המסמך',
  'file.previewPrevious': 'העמוד הקודם',
  'file.previewNext': 'העמוד הבא',
  'file.previewPage': 'עמוד {page} מתוך {count}',
  'file.previewZoomOut': 'הקטנה',
  'file.previewZoomIn': 'הגדלה',
  'file.previewLoading': 'מכין תצוגה…',
  'file.previewUnavailable': 'לא ניתן לפתוח את התצוגה. הקובץ השמור לא השתנה.',
  'file.previewCanvas': 'עמוד מסמך {page}',
  'file.downloadFailed': 'לא ניתן היה להוריד את {name}. הקובץ לא השתנה.',

  'file.downloadOne': 'הורדת הקובץ שנבחר',
  'file.downloadSelected': 'הורדת {count} קבצים',
  'file.downloadEach': 'הורדה בנפרד',
  'file.downloadArchive': 'הורדה כארכיון אחד',
  'file.downloadWorking': 'מוריד את הקבצים שנבחרו…',
  'file.downloadStopped':
    'לא ניתן היה להוריד את {name}, ולכן שאר הקבצים לא נמשכו. הקבצים לא השתנו.',
  'file.downloadArchiveFailed': 'לא ניתן היה לבנות את הארכיון. הקבצים לא השתנו.',

  'file.deleteNamed': 'מחיקת {name}',
  'file.deleteSelected': 'מחיקת הנבחרים: {count}',
  'file.deleteTitle': 'למחוק את קובצי המטופל שנבחרו?',
  'file.deleteKeep': 'השארת הקבצים',
  'file.deleteConfirm': 'מחיקה לצמיתות',
  'file.deleting': 'מוחק…',
  'file.deleteFilesTitle': 'קבצים ועותקים נגזרים',
  'file.deleteFilesDetail': 'נתוני המקור וכל עותק שעובד יימחקו פיזית. רשומת הביקורת והסיבה שלך יישארו.',
  'file.deleteReason': 'סיבת המחיקה',
  'file.deleteReasonHint': 'שדה חובה שנרשם עם שמך בהיסטוריית הביקורת.',
  'file.deleteReasonWrongPatient': 'הועלה למטופל הלא נכון',
  'file.deleteReasonDuplicate': 'העלאה כפולה',
  'file.deleteReasonMistake': 'הועלה בטעות',
  'file.deleteReasonOther': 'סיבה אחרת',
  'file.deleteReasonDetail': 'הסיבה במילים שלך',
  'file.deleteFailed': 'לא ניתן היה להשלים את המחיקה. פריטים שכבר נמחקו ייעלמו; פריטים שלא השתנו יישארו זמינים.',
  'file.deleteRefusedChanged': 'השורה השתנתה בזמן שהשאלה הייתה פתוחה, ולכן היא לא נמחקה. שורות שנמחקו לפניה כבר אינן קיימות; יש לפתוח את המחיקה מחדש ולפעול על מה שהשורה היא עכשיו.',
  'file.deleteRefusedGone': 'השורה כבר אינה קיימת, ולכן לא נותר מה למחוק עבורה. גם שורות שנמחקו לפניה כבר אינן קיימות.',
  'file.deleteRefusedForbidden': 'לחשבון הזה אין הרשאה למחוק את השורה. היא לא נמחקה; שורות שנמחקו לפניה כבר אינן קיימות.',
  'file.deleteRefusedInvalid': 'המחיקה נדחתה כפי שנוסחה, ולכן השורה לא נמחקה. יש לבדוק את הסיבה ולנסות שוב; שורות שנמחקו לפניה כבר אינן קיימות.',
  'file.deleteRefusedUnavailable': 'האחסון לא הגיב עבור השורה, ולכן היא לא נמחקה. שורות שנמחקו לפניה כבר אינן קיימות, וכדאי לנסות שוב.',

  'file.comment': 'הערה',
  'file.commentLoading': 'טוען פרטים',
  'file.commentUnavailable': 'הפרטים אינם זמינים',
  'file.noComment': 'לא נרשמה הערה',
  'file.mediaType': 'סוג מאוחסן',
  'file.digest': 'סכום ביקורת',
  'file.noDocumentDate': 'לא תועד',
  'file.technicalTitle': 'פרטים טכניים',
  'file.classificationNotSet': 'לא נקבעו קבוצה ותוכן',
  'file.classifyAI': 'סיווג AI',
  'file.classifyAIRequesting': 'שולח בקשה',
  'file.classifyAIOverwriteTitle': 'להחליף את סיווג הקובץ?',
  'file.classifyAIOverwriteDescription':
    'ה-AI יסווג את הקובץ מחדש ועשוי להחליף את הקבוצה והתוכן הנוכחיים.',
  'file.classifyAIOverwriteCancel': 'להשאיר את הנוכחי',
  'file.classifyAIOverwriteConfirm': 'לסווג מחדש עם AI',

  'file.editMetadata': 'עריכת הפרטים',
  'file.editFormLabel': 'עריכת הפרטים עבור {name}',
  'file.editTitle': 'כותרת',
  'file.editTitleHint': 'השאר ריק כדי להציג את שם הקובץ המאוחסן.',
  'file.editDocumentDate': 'תאריך המסמך',
  'file.editDocumentDateHint': 'השאר ריק אם למסמך אין תאריך.',
  'file.editComment': 'הערה',
  'file.editSave': 'שמירת הפרטים',
  'file.editSaving': 'שומר',
  'file.editCancel': 'ביטול',
  'file.editStaleRevision': 'מישהו אחר שינה את הפרטים האלה בזמן שערכת אותם.',
  'file.editStaleRevisionDetail':
    'טען את הגרסה השמורה כדי לראות מה מאוחסן כעת, ולאחר מכן החל עליה את השינוי שלך.',
  'file.editReload': 'טעינת הגרסה השמורה',
  'file.editReloadFailed': 'לא ניתן היה לטעון את הגרסה השמורה. השינויים שלך עדיין כאן.',
  'file.editInvalid': 'בדוק את הפרטים ונסה שוב.',
  'file.editForbidden': 'אין לך הרשאה לערוך את הקובץ הזה.',
  'file.editGone': 'הקובץ הזה אינו זמין עוד.',
  'file.editSessionEnded': 'ההפעלה שלך הסתיימה. היכנס שוב כדי להמשיך.',
  'file.editUnavailable': 'לא ניתן היה לשמור את הפרטים כעת. נסה שוב מאוחר יותר.',
  'file.editUnsavedTitle': 'פרטים שלא נשמרו',
  'file.editUnsavedDetail': 'שינית את הפרטים של הקובץ הזה ולא שמרת אותם.',
  'file.editKeepEditing': 'המשך עריכה',
  'file.editDiscard': 'ביטול השינויים',
  'file.unsavedIntent': 'פרטי קובץ',

  'file.linksTitle': 'רשומות רפואיות',
  'file.linksNone': 'אין רשומות רפואיות המקושרות לקובץ זה.',
  'file.linksAdd': 'הוספת רשומה רפואית',
  'file.linkPick': 'קישור לרשומה…',
  'file.linkExternal': 'חיצונית',
  'file.linkedAt': 'קושר {date}',
  'file.unlinkRecord': 'ביטול הקישור אל {name}',
  'file.unlinkAction': 'ביטול קישור',
  'file.linkAddTitle': 'קישור לרשומה',
  'file.linkRecord': 'קישור אל {name}',
  'file.linkAction': 'קישור',
  'file.linkWorking': 'מבצע',
  'file.linkRecordsLoading': 'טוען רשומות',
  'file.linkRecordsUnavailable': 'רשימת הרשומות אינה זמינה',
  'file.linkNoneAvailable': 'למטופל זה אין רשומות סופיות נוספות לקישור.',
  'file.linkNoneOnThisPage': 'אין רשומות נוספות בעמוד הזה. הצג רשומות ישנות יותר כדי למצוא עוד.',
  'file.linkMoreRecords': 'הצגת רשומות ישנות יותר',
  'file.recordFinalizedAt': 'הופק סופית',
  'file.recordStatusFinal': 'סופית',
  'file.linkForbidden': 'אין לך הרשאה לשנות קישורי רשומות עבור הקובץ הזה.',
  'file.linkGone': 'לא ניתן היה לשנות את הקישור. הקובץ או הרשומה אינם זמינים עוד.',
  'file.linkUnavailable': 'לא ניתן היה לשנות את קישור הרשומה כעת. נסה שוב מאוחר יותר.',

  'file.columnDerived': 'נגזרות',
  'file.derivedDownloadTranslation': 'הורדת התרגום של {name}',
  'file.derivedDownloadTranslationIn': 'הורדת התרגום ({languages}) של {name}',
  'file.derivedDownloadDeidentified': 'הורדת העותק האנונימי של {name}',
  'file.derivedDownloadOcr': 'הורדת הטקסט המזוהה של {name}',
  'file.derivedPendingTranslation': 'תרגום בתהליך',
  'file.derivedPendingDeidentified': 'עותק אנונימי בתהליך',
  'file.derivedPendingOcr': 'זיהוי טקסט בתהליך',
  'file.derivedAttentionTranslation': 'לא ניתן להגיש את התרגום',
  'file.derivedAttentionDeidentified': 'לא ניתן להגיש את העותק האנונימי',
  'file.derivedAttentionOcr': 'לא ניתן להגיש את הטקסט המזוהה',
  'file.derivedNoneTranslation': 'אין תרגום',
  'file.derivedNoneDeidentified': 'אין עותק אנונימי',
  'file.derivedNoneOcr': 'אין טקסט מזוהה',
  'file.derivedFilterTranslation': 'יש תרגום',
  'file.derivedFilterDeidentified': 'יש עותק אנונימי',
  'file.derivedFilterOcr': 'יש טקסט מזוהה',
  'file.derivedFilterNone': 'ללא מסמכים נגזרים',

  'file.derivedTitle': 'מסמכים נגזרים',
  'file.deriveNotConnected': 'עיבוד אינו מחובר לקובץ הזה.',
  'file.deriveLoading': 'טוען מסמכים נגזרים',
  'file.deriveUnavailable': 'מסמכים נגזרים אינם זמינים',
  'file.deriveNone': 'אין עדיין מסמכים נגזרים.',
  'file.derivedKindTranslation': 'תרגום',
  'file.derivedKindInLanguage': '{kind} ({language})',
  'file.derivedKindDeidentified': 'עותק אנונימי',
  'file.derivedKindOcr': 'טקסט מזוהה',
  'file.deriveDownload': 'הורדה',
  'file.deriveStatePending': 'בעיבוד',
  'file.deriveStatePurged': 'נמחק',
  'file.deriveStateBroken': 'לא ניתן להגיש את העותק השמור',
  'file.deriveStateRecreating': 'נוצר מחדש',
  'file.deriveStateFailed': 'הניסיון האחרון נכשל',
  'file.deriveRetry': 'ניסיון חוזר',
  'file.deriveRetryNamed': 'ניסיון חוזר: {name}',
  'file.deriveMadeByProcessing': 'פתיחת העיבוד',
  'file.deriveExtractEntries': 'חילוץ רשומות רפואיות…',
  // 'סקירה', not the everyday word for review: that word is a published SOAP
  // protocol label and the bundle gate rejects any copy that embeds it.
  'file.derivePurge': 'מחיקת עותק',
  'file.derivePurgeNamed': 'מחיקת העותק השמור של {name}',
  'file.derivePurgeConfirm': 'למחוק את העותק השמור?',
  'file.derivePurgeYes': 'מחיקה',
  'file.derivePurgeKeep': 'השארה',
  'file.deriveRecreateNamed': 'יצירה מחדש של {name}',
  'file.deriveAdd': 'הוספת מסמך נגזר',
  'file.deriveAddSummary': 'סיכום',
  'file.deriveSourceTitle': 'מקור',
  'file.deriveSourceOriginal': 'מהמקור',
  'file.deriveSourceDeidentified': 'מהעותק האנונימי',
  'file.deriveDeidentify': 'אנונימיזציה של המקור תחילה',
  'file.runStatusQueued': 'בתור',
  'file.runStatusRunning': 'בעיבוד',
  'file.runStatusRetryWait': 'ניסיון חוזר',
  'file.runStatusSucceeded': 'הושלם',
  'file.runStatusPartiallySucceeded': 'הושלם חלקית',
  'file.runStatusFailed': 'נכשל',
  'file.runStatusCanceled': 'בוטל',
  'file.deriveCancel': 'ביטול העיבוד',
  'file.deriveCancelRequested': 'מבטל',
  'file.deriveRunFailure': 'העיבוד נכשל',
  'file.failure.canceled': 'בוטל.',
  'file.failure.dependency': 'שלב קודם בעיבוד זה נכשל.',
  'file.failure.formatUnsupported': 'לא ניתן לפתוח את הקובץ: הפורמט אינו נתמך, או שהקובץ פגום או גדול מדי.',
  'file.failure.noText': 'לא נמצא בקובץ זה טקסט שניתן לקרוא.',
  'file.failure.notConfigured': 'שירות העיבוד אינו מוגדר במרפאה.',
  'file.failure.serviceFailed': 'שירות העיבוד נכשל; ניסיון חוזר עשוי לעזור.',
  'file.failure.integrity': 'הקובץ השמור אינו תואם לסכום הביקורת שלו.',
  'file.failure.tooLarge': 'הקובץ גדול מדי לעיבוד.',
  'file.failure.provider': 'שירות ה-AI לא ענה; ניסיון חוזר עשוי לעזור.',
  'file.failure.resultInvalid': 'שירות ה-AI החזיר תוצאה שאינה שמישה; ניסיון חוזר עשוי לעזור.',
  'file.failure.aiUnavailable': 'פונקציית AI זו כבויה במרפאה או שאין לה מפתח.',
  'file.failure.unknown': 'העיבוד לא הסתיים.',
  'file.deriveStartFailed': 'לא ניתן היה להתחיל את העיבוד. הקובץ ללא שינוי.',
  'file.deriveCancelFailed': 'לא ניתן היה למסור את הביטול. העיבוד נמשך.',
  'file.derivePurgeFailed': 'לא ניתן היה למחוק את העותק השמור. ייתכן שהוא עדיין משמש עיבוד פעיל.',
  'file.deriveDownloadFailed': 'לא ניתן היה להוריד את העותק. הנתונים השמורים ללא שינוי.',

  /* The processing panel over the ticked rows: what was selected, what can be
     done with it, and the act that starts it. */
  'file.processOne': 'עיבוד קובץ אחד',
  'file.processSelected': 'עיבוד {count} קבצים',
  'file.processTitle': 'עיבוד קבצים',
  'file.processSelection': 'נבחרו',
  'file.processSelectedFolders': 'תיקיות',
  'file.processSelectedFiles': 'קבצים',
  'file.processSelectedStudies': 'מחקרי הדמיה',
  'file.processFoldersIncluded': 'כלולים הקבצים שבתיקיות שנבחרו ובכל תתי־התיקיות שלהן.',
  'file.processActions': 'מה לעשות',
  'file.processEntries': 'חילוץ רשומות רפואיות',
  'file.processEntriesHint': 'הבינה המלאכותית תציע רשומה לכל אירוע טיפול; כל רשומה נבדקת על ידיך לפני השמירה.',
  'file.processSummarize': 'סיכום',
  'file.processSummarizeHint': 'סיכום אחד לכל הקבצים, בשפה שנבחרה.',
  'file.processTranslate': 'תרגום',
  'file.processTranslateHint': 'עותק מתורגם של כל קובץ, בשפה שנבחרה.',
  'file.processOcr': 'זיהוי טקסט',
  'file.processOcrHint': 'הטקסט של סריקות ותמונות, לחיפוש ולקריאה.',
  'file.processDeidentify': 'אנונימיזציה',
  'file.processDeidentifyHint': 'עותק PDF של כל קובץ ללא הפרטים האישיים של המטופל.',
  'file.processLanguage': 'שפת התוצאה',
  'file.processChoose': 'יש לבחור מה לעשות בקבצים.',
  'file.processStart': 'התחלה',
  'file.processStarting': 'מתחיל…',
  'file.processNotReady': 'עדיין לא זמינים לעיבוד',
  'file.processNone': 'אף אחד מהקבצים שנבחרו אינו זמין עדיין לעיבוד.',
  'file.processSteps': 'שלבים שהושלמו: {done} מתוך {total}',
  'file.addFile': 'הוספת קובץ',
  'file.addFolder': 'הוספת תיקייה',
  'file.foldersOpen': 'עץ תיקיות',
  'file.foldersTitle': 'תיקיות',
  'file.foldersSearch': 'חיפוש תיקייה או קובץ',
  'file.foldersSearchPlaceholder': 'חיפוש בתיקיות ובקבצים',
  'file.foldersShowEverything': 'הכול',
  'file.foldersShowFolders': 'תיקיות בלבד',
  'file.foldersShowFiles': 'קבצים בלבד',
  'file.foldersLoading': 'קוראים את התיקיות…',
  'file.foldersFilesLoading': 'קוראים את הקבצים…',
  'file.foldersUnavailable': 'לא ניתן לקרוא את התיקיות.',
  'file.foldersEmpty': 'אין תיקיות עדיין. עד שתיווצר תיקייה הכול מוצג ברשימה אחת.',
  'file.foldersEmptyAdd':
    'אין תיקיות עדיין. עד שתיווצר תיקייה הכול מוצג ברשימה אחת. את הראשונה יוצרים בלחצן "הוספת תיקייה" בראש החלונית.',
  'file.foldersNoMatches': 'אין תיקייה או קובץ מתאימים.',
  'file.foldersLevelEmpty': 'עדיין לא תויק כאן דבר.',
  'file.foldersLevelFilesOnly': 'כאן מתויקים קבצים בלבד, והתצוגה מציגה תיקיות.',
  'file.foldersAll': 'כל הקבצים',
  'file.foldersUnfiled': 'ללא תיקייה',
  'file.foldersNewName': 'תיקייה חדשה',
  'file.foldersAddTop': 'הוספת תיקייה',
  'file.foldersAddHere': 'הוספת תיקייה בפנים',
  'file.foldersRename': 'שינוי שם',
  'file.foldersMoveEarlier': 'העברה למעלה',
  'file.foldersMoveLater': 'העברה למטה',
  'file.foldersMoveToTop': 'העברה לרמה העליונה',
  'file.foldersDelete': 'מחיקת תיקייה',
  'file.foldersOpenFile': 'פתיחה',
  'file.foldersUnfile': 'הוצאה מהתיקייה',
  'file.foldersMenu': 'פעולות: {name}',
  'file.foldersDeleteTitle': 'למחוק את {name}?',
  'file.foldersDeleteDescription': 'התיקייה והתיקיות שבתוכה יימחקו. הקבצים והמחקרים שלהן יישארו בתיק ללא תיקייה.',
  'file.foldersDeleteCancel': 'להשאיר',
  'file.foldersDeleteConfirm': 'מחיקה',
  'file.foldersDeleting': 'מוחקים…',
  'file.foldersStale': 'התיקיות שונו במקום אחר ונקראו מחדש. נסו שוב.',
  'file.foldersNameExists': 'תיקייה בשם זה כבר קיימת כאן.',
  'file.foldersCycle': 'אי אפשר להעביר תיקייה לתוך עצמה.',
  'file.foldersDepth': 'אי אפשר לקנן תיקיות עמוק יותר כאן.',
  'file.foldersForbidden': 'לחשבון שלכם אין הרשאה לשנות את התיקיות.',
  'file.foldersFailed': 'לא ניתן לשנות את התיקיות.',
  'file.foldersRoot': 'שורש',
  'file.foldersUpTo': 'למעלה אל {name}',
  'file.folderOpenNamed': 'פתיחת התיקייה {name}',
  'file.columnFolder': 'תיקייה',
  'file.locationLabel': 'המיקום בתיקיות',
  'file.locationBack': 'אחורה',
  'file.locationForward': 'קדימה',
  'file.locationUp': 'תיקייה אחת למעלה',
  'file.folderNewKey': 'תיקייה חדשה (F7)',
  'file.searchDeep': 'חיפוש בכל התיקיות המקוננות',
  'file.folderCreateTitle': 'תיקייה חדשה',
  'file.folderRenameTitle': 'שינוי שם התיקייה',
  'file.folderNameLabel': 'שם התיקייה',
  'file.folderNameCancel': 'ביטול',
  'file.folderCreateConfirm': 'יצירה',
  'file.folderRenameConfirm': 'שינוי שם',
  'file.folderRenameNamed': 'שינוי שם התיקייה {name}',
  'file.folderDeleteNamed': 'מחיקת התיקייה {name}',
  'file.folderHolds': 'תיקיות: {folders}, קבצים: {files}',
  'file.folderHoldsEmpty': 'ריקה',
  'file.folderEmpty': 'התיקייה ריקה',
  'file.folderEmptyDetail': 'אפשר לגרור לכאן קבצים או להעלות אותם כשהתיקייה פתוחה.',
  'file.menuOpen': 'פתיחה',
  'file.menuProperties': 'מאפיינים',
  'file.menuDownload': 'הורדה',
  'file.menuRename': 'שינוי שם',
  'file.menuMove': 'העברה לתיקייה…',
  'file.menuDelete': 'מחיקה',
  'file.menuSelected': 'פעולות על הנבחרים: {count}',
  'file.deleteFoldersTitle': 'תיקיות עם כל תוכנן',
  'file.deleteFoldersDetail': 'כל תיקייה נמחקת יחד עם התיקיות שבתוכה, וכל קובץ ובדיקה שבתוכן נמחקים פיזית, כמתואר להלן.',
  'file.deleteFoldersReading': 'קוראים את תוכן התיקיות…',
  'file.deleteFoldersUnread': 'לא ניתן היה לקרוא את תוכן התיקיות, ולכן אי אפשר למחוק אותן כעת.',
  'file.deleteFoldersBlocked': 'בתוך התיקיות יש פריטים שאין לך הרשאה למחוק או שעדיין אי אפשר למחוק: {count}. יש להוציא אותם מהתיקיות קודם.',
  'file.downloadGathering': 'קוראים את תוכן התיקיות שנבחרו…',
  'file.foldersContentsUnread': 'לא ניתן היה לקרוא את תוכן התיקיות שנבחרו, ולכן אי אפשר להוריד או לעבד אותן כעת.',
  'file.foldersContentsRetry': 'לקרוא שוב',
  'file.moveSelected': 'לתיקייה: {count}',
  'file.moveSelectedTitle': 'העברה לתיקייה',
  'file.moveSelectedDescription': 'נבחרו לתיוק: {count}. בחרו לאן להעביר אותם.',
  'file.moveSelectedTarget': 'תיקייה',
  'file.moveSelectedUnfiled': 'ללא תיקייה',
  'file.moveSelectedSkipped': 'לא ייכללו: {count} — עדיין בהעברה או טרם נרשמו, ואי אפשר לתייק אותם.',
  'file.moveSelectedCancel': 'ביטול',
  'file.moveSelectedConfirm': 'העברה: {count}',
  'file.opTypeDeidentify': 'אנונימיזציה',
  'file.opTypeOcr': 'זיהוי טקסט',
  'file.opTypeTranslate': 'תרגום',
  'file.opTypeSummarize': 'סיכום',
  'file.opTypeRender': 'יצירת מסמך',
  'file.opTypePurge': 'ניקוי',

  'upload.title': 'הוספת קובץ רפואי',
  'upload.titleBundle': 'הוספת קבצים רפואיים',
  'upload.limit': 'מתקבלים קבצים בגודל של עד {size}.',
  'upload.filesSelected': 'נבחרו {count} קבצים',
  'upload.filesPartial':
    'כבר נשמרו: {stored}. נותרו להעלאה: {count}. הקבצים שנשמרו לא נשלחים שוב.',
  'upload.progressLabel': 'התקדמות ההעלאה',
  'upload.progressIndeterminate': 'מעלה. לא ניתן להציג כמה כבר נשלח.',
  'upload.progressIndeterminateBundle': 'מעלה {count} קבצים. לא ניתן להציג כמה כבר נשלח.',
  'upload.unsavedIntent': 'העלאת קובץ',
  'upload.fieldTitle': 'כותרת',
  'upload.fieldServiceDate': 'תאריך השירות הקליני',
  'upload.fieldFile': 'קובץ',
  'upload.fieldFolder': 'תיקייה',
  'upload.folderUnfiled': 'ללא תיקייה',
  'upload.aiClassification': 'לתת ל-AI לבחור קבוצה ותוכן אחרי ההעלאה',
  'upload.submit': 'העלאה',
  'upload.submitting': 'מעלה',
  'upload.accepted': 'הקובץ התקבל ונוסף לקבצי המטופל.',
  'upload.acceptedBundle': '{count} קבצים התקבלו ונוספו לקבצי המטופל.',
  'upload.detailsTitle': 'פרטים',
  'upload.detailsHint': 'לא חובה — אפשר למלא כל שדה, חלק מהם או אף אחד.',
  'upload.detailsBundleHint': 'לא חובה — אפשר למלא כל שדה, חלק מהם או אף אחד. פרטים שמולאו יחולו על כל קובץ שנבחר.',
  'upload.discardTitle': 'הקובץ לא הועלה',
  'upload.discardBundleTitle': 'הקבצים לא הועלו',
  'upload.discardDetail': 'התחלת להוסיף את הקובץ הזה ולא העלית אותו.',
  'upload.discardBundleDetail': 'התחלת להוסיף את הקבצים האלה ולא העלית אותם.',
  'upload.discardRemainingDetail':
    'הקבצים שברשימה לא הועלו. קבצים שכבר נשמרו: {count} — הם נשארים בקבצי המטופל.',
  'upload.keepEditing': 'המשך עריכה',
  'upload.leavingTitle': 'ההעלאה עדיין פועלת',
  'upload.leavingDetail':
    'הקבצים שעדיין נשלחים יסתיימו ויופיעו בקבצי המטופל. הסגירה אינה מבטלת דבר, אך התוצאה של כל אחד מהם לא תוצג כאן יותר.',
  'upload.leavingWait': 'להישאר כאן',
  'upload.leavingConfirm': 'לסגור בכל זאת',
  'upload.discardConfirm': 'ביטול הוספת הקובץ',
  'upload.discardBundleConfirm': 'ביטול הוספת הקבצים',
  'upload.bundleRefused': 'קבצים שלא התקבלו: {count} מתוך {total}. הסיבה מופיעה ליד כל קובץ למעלה.',
  'upload.unsupportedType': 'לא ניתן להעלות קובץ זה כקובץ בינארי.',
  'upload.tooLarge': 'הקובץ גדול מ-300 MB.',
  'upload.tooLargeUnstated': 'הקובץ גדול מדי להעלאה.',
  'upload.conflict': 'העלאה זו כבר נשלחה עם פרטים אחרים. התחל העלאה חדשה.',
  'upload.invalidInput': 'בדוק את הפרטים ונסה שוב.',
  'upload.sessionEnded': 'ההפעלה הסתיימה. היכנס שוב כדי להעלות.',
  'upload.forbidden': 'אין לך הרשאה להוסיף קבצים למטופל זה.',
  'upload.patientGone': 'המטופל אינו זמין עוד.',
  'upload.unavailable': 'לא ניתן היה להעלות את הקובץ כעת. נסה שוב מאוחר יותר.',


  /* 'AI' STAYS LATIN AND 'Auto' DOES NOT.
     This catalogue writes the loanword throughout — סיווג AI, ה-AI,
     פרומפטים של AI — so AI is the product's Hebrew vocabulary and translating
     one instance would disagree with twenty. 'Auto' is not vocabulary, it is
     an English word, and it left this option the only one in a Hebrew Type
     funnel with no Hebrew in it. The adjective follows the noun; the hint
     above names this label verbatim, and a test holds the two together.

     THE VALUE OPENS WITH AN INVISIBLE RLM (U+200F), and it is load-bearing.
     The Type funnel draws every option inside a `bdi`, which resolves its
     direction from the FIRST STRONG CHARACTER of the value it contains — so a
     value starting with Latin `AI` lays itself out left-to-right inside an RTL
     page, and a reader scanning right to left meets אוטומטי before AI: the
     adjective before its noun, the reverse of what is written here. The mark
     declares the value's own base direction before the Latin can. It is the
     same move `googleSignIn.hint` makes with an LRM in the other direction,
     and jsdom has no bidi, so no rendering test can see its absence — the
     catalogue gate reads the first strong character instead. */
  'file.classificationPending': 'הסיווג באמצעות AI ממתין',
  'file.classificationFailed': 'סיווג ה-AI דורש עיון',
  'file.classificationReclassifying': 'מסווג מחדש',

  'emrJournal.title': 'יומן רשומות רפואיות',
  'emrJournal.tableLabel': 'יומן הרשומות הרפואיות של המטופל',
  'emrJournal.loading': 'טוען את יומן הרשומות',
  'emrJournal.loadFailed': 'לא ניתן היה לטעון את יומן הרשומות',
  'emrJournal.forbidden': 'אין לך הרשאה לצפות ברשומות הרפואיות',
  'emrJournal.forbiddenDetail': 'שאר כרטיס המטופל עדיין זמין.',
  'emrJournal.empty': 'אין עדיין רישומים ביומן',
  'emrJournal.emptyDetail': 'מפגשים שיירשמו למטופל זה יופיעו כאן.',
  'emrJournal.emptyFiltered': 'אין רישומים התואמים לסינון',
  'emrJournal.emptyFilteredDetail': 'נקה את הסינון כדי לראות את היומן כולו.',
  'emrJournal.countOne': 'רישום אחד',
  'emrJournal.countMany': '{count} רישומים',
  'emrJournal.columnStarted': 'תאריך ושעה',
  'emrJournal.columnType': 'סוג רישום',
  'emrJournal.columnPractitioner': 'מטפל',
  'emrJournal.columnStatus': 'סטטוס',
  'emrJournal.columnCase': 'תיק',
  'emrJournal.columnSections': 'מקטעים',
  'emrJournal.typeScheduled': 'מפגש מתוכנן',
  'emrJournal.typeUnscheduled': 'מפגש לא מתוכנן',
  'emrJournal.externalManual': 'הוזן ידנית',
  'emrJournal.externalAI': 'ממסמכים (בינה מלאכותית)',
  'emrJournal.externalNoDate': 'לא צוין תאריך',
  'emrJournal.sectionProtocol': 'פרוטוקול',
  'emrJournal.sectionDiagnoses': 'אבחנות',
  'emrJournal.sectionAbsent': 'טרם החל',
  'emrJournal.sectionIncomplete': 'חסר',
  'emrJournal.sectionComplete': 'הושלם',
  'emrJournal.sectionState': '{section}: {state}',
  'emrJournal.sectionFiles': 'קבצים ({count})',
  'emrJournal.filterFrom': 'מתאריך',
  'emrJournal.filterTo': 'עד תאריך',
  'emrJournal.criterionStarted': 'מותאם לשעת ההתחלה כפי שמוצגת ולקוד המפגש.',
  'emrJournal.criterionCase': 'מותאם לקוד התיק ולכותרתו.',
  'emrJournal.caseFacetOption': '{code} — {title}',
  'emrJournal.filterEnteredInError': 'הצג רישומים שנרשמו בטעות',

  'emrEntry.title': 'הוספת רישום',
  'emrEntry.branch': 'מקום הטיפול',
  'emrEntry.workplaceRequired': 'בחירת מקום עבודה',
  'emrEntry.workplaceUnavailable': 'מקום העבודה השתנה או אינו זמין עוד. יש לבחור מחדש מקום עבודה זמין.',
  'emrEntry.workplace': 'מקום עבודה',
  'emrEntry.workplaceNone': 'לא נבחר',
  'emrEntry.workplaceChange': 'החלפה',
  'emrEntry.workplaceChangeLabel': 'החלפת מקום עבודה',
  'emrEntry.workplaceChoose': 'בחירת מקום עבודה',
  'emrEntry.branchRequired': 'בחירת סניף',
  'emrEntry.branchUnavailable': 'הסניף אינו זמין עוד. יש לבחור סניף אחר.',
  'emrEntry.detail': 'מפגש חדש והפרוטוקול הראשי שלו ייווצרו יחד.',
  'emrEntry.placeLegend': 'היכן ניתן הטיפול',
  'emrEntry.placeClinic': 'במרפאה שלנו',
  'emrEntry.placeClinicHint': 'פותח מפגש עם הפרוטוקול הראשי שלו במסגרת מקרה.',
  'emrEntry.placeOutside': 'במרפאה אחרת',
  'emrEntry.placeOutsideHint': 'מתעד טיפול שהמטופל קיבל במקום אחר, לפי פרוטוקול הרשומה החיצונית.',
  'emrEntry.externalDetail': 'נוצרת טיוטת רשומה חיצונית ונפתחת: יש למלא ולהשלים אותה שם.',
  'emrEntry.externalProtocolFixed': 'טיפול שניתן במקום אחר מתועד תמיד לפי פרוטוקול זה.',
  'emrEntry.caseLegend': 'תיק',
  'emrEntry.caseNoneOpen': 'למטופל זה אין תיק פתוח, ולכן רישום זה פותח תיק חדש.',
  'emrEntry.caseChoose': 'בחר לאיזה תיק שייך רישום זה.',
  'emrEntry.submit': 'יצירת רישום',
  'emrEntry.submitting': 'יוצר…',
  'emrEntry.failed': 'לא ניתן היה ליצור את הרישום. נסה שוב.',
  'emrEntry.forbidden': 'אין לך הרשאה להוסיף רישום.',
  'emrEntry.unavailable': 'השירות אינו זמין. נסה שוב בקרוב.',
  'emrEntry.conflict': 'הרישום לא נוצר. רענן את היומן ונסה שוב.',
  'emrEntry.caseUnavailable': 'לא ניתן עוד להוסיף רישום לתיק זה. בחר תיק אחר או צור חדש.',
  'emrEntry.gone': 'מטופל זה אינו זמין עוד.',
  'emrEntry.sessionEnded': 'ההתחברות הסתיימה. התחבר שוב כדי להמשיך.',

  'file.group': 'קבוצה',
  'file.content': 'תוכן',
  'file.groupUnchosen': 'בחירת קבוצה',
  'file.contentUnchosen': 'בחירת תוכן',
  'provenance.human': 'נבחר על ידי אדם',
  'provenance.humanDraft': 'נבחר על ידיך. טרם נשמר.',
  'provenance.ai': 'נבחר על ידי AI',
  'provenance.aiConfirmed': 'נבחר על ידי AI ואושר על ידי אדם',
  'provenance.aiConfirmedDraft': 'נבחר על ידי AI ואושר על ידיך. טרם נשמר.',
  'provenance.system': 'הוגדר על ידי המערכת',
  'provenance.unknown': 'מקור הבחירה לא נרשם',
  'fileGroup.medical': 'רפואיים',
  'fileGroup.financial': 'כספיים',
  'fileGroup.identity': 'זיהוי',
  'fileGroup.administrative': 'מנהליים',
  'fileGroup.collection': 'אוסף מסמכים',
  'fileGroup.other': 'אחרים',
  'fileContent.medical_conclusion': 'חוות דעת רפואית',
  'fileContent.discharge_summary': 'סיכום שחרור',
  'fileContent.clinical_referral': 'הפניה',
  'fileContent.laboratory_results': 'תוצאות מעבדה',
  'fileContent.study_report': 'דוח בדיקה אבחנתית',
  'fileContent.medical_images': 'דימות רפואי',
  'fileContent.prescription': 'מרשם',
  'fileContent.treatment_plan': 'תוכנית טיפול',
  'fileContent.vaccination_record': 'רישום חיסונים',
  'fileContent.medical_summary': 'סיכום רפואי',
  'fileContent.invoice': 'חשבונית',
  'fileContent.treatment_cost_estimate': 'הערכת עלות טיפול',
  'fileContent.payment_guarantee': 'התחייבות לתשלום',
  'fileContent.payment_receipt': 'אישור תשלום',
  'fileContent.insurance_policy': 'פוליסת ביטוח',
  'fileContent.passport': 'דרכון',
  'fileContent.identity_card': 'תעודת זהות',
  'fileContent.driving_licence': 'רישיון נהיגה',
  'fileContent.residence_permit': 'אישור תושבות',
  'fileContent.agreement': 'הסכם',
  'fileContent.consent': 'הסכמה',
  'fileContent.appointment_confirmation': 'אישור תור',
  'fileContent.travel_booking': 'כרטיסים והזמנות',
  'fileContent.correspondence': 'התכתבות',
}
